/* @jsx h */
import { computed, defineComponent, h, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StudioCampaign, StudioChannel } from "../types";
import { STUDIO_CHANNELS } from "../types";
import { STUB_SEGMENTS } from "../stub";
import { CHANNEL_META } from "../shared";
import { checkCampaign, worstLevel } from "../checks";
import { EmailEditor } from "../email/EmailEditor";
import { WhatsAppEditor } from "../channels/WhatsAppEditor";
import { SmsEditor } from "../channels/SmsEditor";
import { PushEditor } from "../channels/PushEditor";

type ContentStepProps = {
  campaign: StudioCampaign;
  onChange: (patch: Partial<StudioCampaign>) => void;
};

const DOT: Record<string, string> = { error: "bg-error", warning: "bg-warning", ok: "bg-success" };

export const ContentStep = defineComponent<ContentStepProps>({
  name: "CampaignContentStep",
  props: ["campaign", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const enabled = computed(() => STUDIO_CHANNELS.filter((c) => props.campaign.channels.includes(c)));
    const active = ref<StudioChannel>(enabled.value[0] ?? "email");

    watch(enabled, (list) => {
      if (!list.includes(active.value) && list.length) active.value = list[0];
    });

    const checks = computed(() =>
      checkCampaign(props.campaign, STUB_SEGMENTS.find((s) => s.id === props.campaign.segment_id)),
    );
    const levelFor = (ch: StudioChannel) => worstLevel(checks.value.filter((c) => c.channel === ch));

    const Editor = () => {
      const c = props.campaign;
      switch (active.value) {
        case "email":
          return <EmailEditor email={c.email} offer={c.offer} onChange={(email) => props.onChange({ email })} />;
        case "whatsapp":
          return <WhatsAppEditor content={c.whatsapp} onChange={(whatsapp) => props.onChange({ whatsapp })} />;
        case "sms":
          return <SmsEditor content={c.sms} onChange={(sms) => props.onChange({ sms })} />;
        case "push":
          return <PushEditor content={c.push} offer={c.offer} onChange={(push) => props.onChange({ push })} />;
      }
    };

    return () => {
      if (!enabled.value.length) {
        return (
          <div class="flex h-full flex-col items-center justify-center gap-2 text-base-content/60">
            <span class="material-symbols-outlined text-4xl">forum</span>
            {t("campaigns.studio_checks.no_channels")}
          </div>
        );
      }
      return (
        <div class="flex h-full min-h-0 flex-col">
          <div role="tablist" class="tabs tabs-lifted shrink-0 bg-base-200 px-4 pt-2">
            {enabled.value.map((ch) => (
              <button
                key={ch}
                role="tab"
                type="button"
                class={["tab gap-2", active.value === ch && "tab-active [--tab-bg:oklch(var(--b1))]"]}
                onClick={() => (active.value = ch)}
              >
                <span class={["material-symbols-outlined text-base", CHANNEL_META[ch].tone]}>{CHANNEL_META[ch].icon}</span>
                {t(CHANNEL_META[ch].label)}
                <span class={["h-2 w-2 rounded-full", DOT[levelFor(ch)]]} />
              </button>
            ))}
          </div>
          <div class="min-h-0 flex-1 bg-base-100">{Editor()}</div>
        </div>
      );
    };
  },
});
